import { useEffect, useRef, useState } from 'react'
import { Capacitor } from '@capacitor/core'
import { Download, Share2, X } from 'lucide-react'
import { OverlayPortal } from '../ModalPortal'
import { fetchCaseReportPdf } from '../../utils/caseReport'
import { loadPdfjs } from '../../utils/pdfjs'
import { saveOrShareBlob } from '../../utils/saveOrShareBlob'
import { OrellaSpinner } from '../OrellaLoader' // s123: the app's one spinner

/**
 * CaseReportViewer — s99 D477 (Dr. Rami's Build 61 TestFlight review, 2026-10-11, mockup
 * "Orella Build 61 Edits" approved).
 *
 * ★ s102 D8 (Dr. Rami list 3, 2026-10-13; reverses D477/D481(5) "view only"): the viewer has
 *   Download and Share, as originally designed. On the phone ONE button opens the share sheet,
 *   which carries "Save to Files" (the download) and the share targets; on the web, Download is a
 *   file download and Share appears where the browser can share files. Both hand over the SAME
 *   server PDF the viewer drew (held in blobRef, never re-fetched). Still no print, no iframe/img.
 * ★ THE AGREEMENT IS THE CALLER'S. The Document Sharing Agreement (VisitPrepDisclaimer) gates
 *   this viewer once per case (visitPrepAgreed on the case); every caller checks it first and
 *   this component never opens on a case that has not agreed (it does not re-check).
 * ★ THE PAGES ARE CANVASES, not an <iframe> or an <img>: iOS WKWebView shows only the first
 *   page of a PDF in an iframe, and a canvas offers no long-press "Save Image".
 * ★ pdf.js IS VENDORED, not a dependency: public/vendor/pdfjs-3.11.174 holds Mozilla's
 *   prebuilt legacy build (Apache-2.0, LICENSE beside it). The npm package pulls an optional
 *   native `canvas` build into every install; this loads the two files only when the viewer opens.
 * The document is the server's (GET /cases/<id>/report.pdf), the same one the doctor received.
 */
// The pdf.js loader lives in utils/pdfjs.js so the stored-document viewer shares it without
// importing this file (and its caseReport → services/api chain).

export default function CaseReportViewer({ open, caseObj, onClose, t, footNote }) {
  const [phase, setPhase] = useState('loading') // loading | ready | error
  const [errStatus, setErrStatus] = useState(0)
  const [pageCount, setPageCount] = useState(0)
  const [attempt, setAttempt] = useState(0)
  const pagesRef = useRef(null)
  const blobRef = useRef(null)
  const [busy, setBusy] = useState(false)
  const tr = (k, d, o) => (t ? t(k, { defaultValue: d, ...(o || {}) }) : d)

  useEffect(() => {
    if (!open || !caseObj || !caseObj.id) return undefined
    let cancelled = false
    let doc = null
    setPhase('loading'); setErrStatus(0); setPageCount(0); blobRef.current = null
    ;(async () => {
      try {
        const [lib, blob] = await Promise.all([loadPdfjs(), fetchCaseReportPdf(caseObj)])
        blobRef.current = blob
        const data = new Uint8Array(await blob.arrayBuffer())
        doc = await lib.getDocument({ data }).promise
        if (cancelled) return
        setPageCount(doc.numPages)
        setPhase('ready')
        await new Promise((r) => requestAnimationFrame(() => r()))
        const host = pagesRef.current
        if (!host) return
        const cssWidth = Math.max(260, host.clientWidth - 28)
        const dpr = Math.min(window.devicePixelRatio || 1, 3)
        for (let n = 1; n <= doc.numPages; n++) {
          if (cancelled) return
          const page = await doc.getPage(n)
          const base = page.getViewport({ scale: 1 })
          const scale = (cssWidth / base.width) * dpr
          const vp = page.getViewport({ scale })
          const canvas = host.querySelector(`canvas[data-page="${n}"]`)
          if (!canvas) continue
          canvas.width = Math.floor(vp.width)
          canvas.height = Math.floor(vp.height)
          canvas.style.width = cssWidth + 'px'
          canvas.style.height = Math.floor(vp.height / dpr) + 'px'
          await page.render({ canvasContext: canvas.getContext('2d'), viewport: vp }).promise
        }
      } catch (e) {
        if (cancelled) return
        setErrStatus((e && e.status) || 0)
        setPhase('error')
      }
    })()
    return () => { cancelled = true; if (doc) { try { doc.destroy() } catch (e) { /* closed */ } } }
  }, [open, caseObj && caseObj.id, attempt])

  if (!open) return null
  const NAVY = '#15354F'
  const isNative = Capacitor.isNativePlatform()
  const num = String(caseObj?.caseNumber || caseObj?.case_number || String(caseObj?.id || '').slice(0, 8)).replace(/^#/, '')
  const filename = `orella-case-report-${num}.pdf`
  const webCanShare = (() => {
    try {
      return !isNative && typeof navigator !== 'undefined' && typeof navigator.canShare === 'function' &&
        navigator.canShare({ files: [new File([new Blob()], filename, { type: 'application/pdf' })] })
    } catch (e) { return false }
  })()
  async function handOver(mode) {
    const blob = blobRef.current
    if (!blob || busy) return
    setBusy(true)
    try {
      if (mode === 'share' && !isNative) {
        try { await navigator.share({ files: [new File([blob], filename, { type: 'application/pdf' })], title: tr('reportViewer.title', 'Your Case Report') }) } catch (e) { /* dismissed */ }
      } else {
        await saveOrShareBlob(blob, filename, { title: tr('reportViewer.title', 'Your Case Report') })
      }
    } finally { setBusy(false) }
  }
  const actionBtn = 'h-9 px-3 rounded-full flex items-center gap-1.5 flex-shrink-0 text-white text-[13px] font-semibold disabled:opacity-50'
  return (
    <OverlayPortal>
      <div className="fixed inset-0 z-[60] flex flex-col" style={{ background: '#5F6B76', animation: 'reportViewerUp 0.3s cubic-bezier(0.2, 0.8, 0.2, 1)' }}
           data-testid="case-report-viewer" onContextMenu={(e) => e.preventDefault()}>
        <style>{'@keyframes reportViewerUp{from{transform:translateY(100%)}to{transform:none}}@media (prefers-reduced-motion:reduce){[data-testid="case-report-viewer"]{animation:none!important}}'}</style>
        <div className="flex items-center justify-between px-4 pb-3 flex-shrink-0"
             style={{ background: NAVY, paddingTop: 'calc(env(safe-area-inset-top, 0px) + 12px)' }}>
          <div className="min-w-0">
            <p className="m-0 text-white text-[16px] font-bold leading-tight">{tr('reportViewer.title', 'Your Case Report')}</p>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
          {!isNative && (
            <button type="button" onClick={() => handOver('download')} disabled={phase !== 'ready' || busy} data-cta="report-viewer-download"
                    className={actionBtn} style={{ background: 'rgba(255,255,255,.14)' }}>
              <Download size={16} color="#fff" />{tr('reportViewer.download', 'Download')}
            </button>
          )}
          {(isNative || webCanShare) && (
            <button type="button" onClick={() => handOver('share')} disabled={phase !== 'ready' || busy} data-cta="report-viewer-share"
                    className={actionBtn} style={{ background: 'rgba(255,255,255,.14)' }}>
              <Share2 size={16} color="#fff" />{tr('reportViewer.share', 'Share')}
            </button>
          )}
          <button type="button" onClick={onClose} aria-label={tr('common.close', 'Close')} data-cta="report-viewer-close"
                  className="w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0" style={{ background: 'rgba(255,255,255,.14)' }}>
            <X size={18} color="#fff" />
          </button>
          </div>
        </div>
        <div ref={pagesRef} className="flex-1 overflow-y-auto px-3.5 py-4 flex flex-col items-center gap-2" style={{ WebkitUserSelect: 'none', userSelect: 'none' }}>
          {phase === 'loading' && (
            <div className="flex-1 flex flex-col items-center justify-center gap-3 text-white text-[13.5px]">
              <OrellaSpinner size={26} />
              {tr('reportViewer.loading', 'Opening your report…')}
            </div>
          )}
          {phase === 'error' && (
            <div className="flex-1 flex flex-col items-center justify-center gap-3 text-center px-6">
              <p className="m-0 text-white text-[14px] leading-snug">
                {errStatus === 422
                  ? tr('shareHub.pdfNeedsName', 'Add your name to your profile to build the PDF.')
                  : tr('reportViewer.failed', "We couldn't open your report. Please try again.")}
              </p>
              <button type="button" onClick={() => setAttempt((a) => a + 1)}
                      className="h-10 px-5 rounded-full bg-white text-[13.5px] font-bold" style={{ color: NAVY }}>
                {tr('reportViewer.retry', 'Try again')}
              </button>
            </div>
          )}
          {phase === 'ready' && Array.from({ length: pageCount }, (_, i) => (
            <div key={i} className="flex flex-col items-center gap-1.5">
              <canvas data-page={i + 1} className="bg-white rounded-[3px]" style={{ boxShadow: '0 3px 10px rgba(0,0,0,.25)', maxWidth: '100%' }} />
              <span className="text-[11px] font-semibold mb-2" style={{ color: '#E5ECF2' }}>
                {tr('reportViewer.page', 'Page {{n}} of {{total}}', { n: i + 1, total: pageCount })}
              </span>
            </div>
          ))}
        </div>
        {footNote ? (
          <div className="flex-shrink-0 text-center text-[11.5px] px-4 pt-2.5" style={{ background: NAVY, color: '#B9CCDC', paddingBottom: 'calc(env(safe-area-inset-bottom, 0px) + 12px)' }}>
            {footNote}
          </div>
        ) : null}
      </div>
    </OverlayPortal>
  )
}
